import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function NorthSomersetCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="North Somerset Pothole Claims | 8,600 Potholes Planned | Fixtyer"
        description="North Somerset projects 8,600 pothole repairs in 2025/26 while its own report admits SCANNER survey data does not match what residents see on local roads. See what the DfT AMBER rating means for your claim."
        canonical="/councils/north-somerset"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'North Somerset', url: '/councils/north-somerset' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'North Somerset has a GREEN condition scorecard — can I still claim?',
            answer: 'Yes. The DfT GREEN condition rating reflects network-wide SCANNER averages, but North Somerset\'s own transparency report admits that data "doesn\'t always reflect what road users are experiencing." Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on a headline scorecard. B and C roads in RED condition rose from 5.0% in 2022 to 6.8% in 2024, and the council filled 6,622 potholes in 2024/25 alone.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 650km — 59% of North Somerset\'s 1,111km road network — and condition data is collected every two years, with 50% surveyed in year one and 50% in year two. Smaller residential roads inaccessible to standard surveys receive an additional survey every four years. At the last five comparable readings, 3.7–4.7% of U-roads were in RED condition. On a minor street, prior reports and photographic evidence matter more than a network average.',
          },
          {
            question: 'Does the council\'s admission about SCANNER data help my claim?',
            answer: 'Potentially, yes. North Somerset states that residents, councillors and its own teams are "reporting more potholes, surface wear, and deterioration — especially on local streets," and describes a "disconnect between the data and people\'s lived experience." It also notes SCANNER surveys "often fail to reflect the deterioration residents and councillors report — especially on local roads." That is documented acknowledgement that official condition records may understate defects on the road where you were damaged.',
          },
          {
            question: 'Why is North Somerset\'s spend scorecard AMBER when it invests heavily in prevention?',
            answer: 'The DfT Spend rating reflects how capital investment compares to the funding allocation. For 2025/26 North Somerset projects £7.15m of capital spend against a £7.15m DfT allocation — matching it exactly, not exceeding it as many GREEN-rated authorities do. Previous years saw higher spend (£9.59m in 2024/25 against £7.99m allocated), but the projected flat match for 2025/26 contributes to the AMBER spend flag. Your claim still turns on the specific defect, not aggregate spend.',
          },
          {
            question: 'North Somerset plans 8,600 pothole repairs in 2025/26 — does that mean the roads are fixed?',
            answer: 'No. The council projects approximately 8,600 pothole repairs in 2025/26, up from 6,622 in 2024/25 and well above the 4,500–6,500 range of earlier years. It attributes the rise to more reported defects and expanded reactive crews — not to a sudden improvement in underlying road condition. A network producing defects at that rate is one where potholes routinely form between inspections.',
          },
          {
            question: 'Who do I claim against for pothole damage in North Somerset?',
            answer: 'North Somerset Council. It is the unitary highway authority for the district\'s 1,111km of local roads, from the A370 and A38 linking Weston-super-Mare, Clevedon and Portishead down to residential unclassified streets. The M5 through North Somerset is managed by National Highways, not the council. Claims are made under Section 41 of the Highways Act 1980; expect a Section 58 defence citing the council\'s inspection regime and asset management strategy.',
          },
          {
            question: 'Should I report the pothole to the council before claiming?',
            answer: 'Yes. North Somerset encourages road users to report defects via its My Roads service at n-somerset.gov.uk/myroads. Reporting creates a dated record on the council\'s system. If the same defect was reported before your incident, or if the council failed to repair it within a reasonable time after notice, that strengthens your claim under Section 58.',
          },
        ]}
      />
      <ServiceSchema
        councilName="North Somerset Council"
        councilSlug="north-somerset"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/north-somerset"
        headline="North Somerset Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">North Somerset</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            8,600 potholes planned — while the council admits its road data doesn&apos;t match what you see
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            North Somerset earns <strong className="text-emerald-400">GREEN</strong> scorecards for road condition
            and best practice, yet the overall DfT rating is <strong className="text-amber-400">AMBER</strong> because
            2025/26 capital spend matches — but does not exceed — its £7.15m allocation. Meanwhile the council&apos;s
            own report describes a <strong className="text-rose-400">disconnect between SCANNER survey data and
            what residents, councillors and repair teams see on local streets</strong>.
          </p>

          {/* Key stat callout */}
          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">8,600</div>
            <div className="text-white font-medium mt-2">Potholes projected for 2025/26</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 6,622 in 2024/25 — a figure the council says reflects more reported defects and expanded
              reactive crews, not healthier roads across its 1,111km network.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER survey data from North Somerset&apos;s own transparency report — A-roads stable,
            B and C roads slipping
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (113km — 10% of network): broadly stable</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">1.4%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">flat from 1.3% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">16.8%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">up from 13.8% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">81.8%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">down from 84.9% in 2020</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Main roads remain in relatively good shape on paper, but amber ratings have crept up and green
              share has fallen. A-roads are just one-tenth of the network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (348km — 31% of network): RED rising</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">5.9%</td>
                    <td className="px-4 py-2 text-center">28.1%</td>
                    <td className="px-4 py-2 text-center">66.0%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">5.7%</td>
                    <td className="px-4 py-2 text-center">27.4%</td>
                    <td className="px-4 py-2 text-center">66.9%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">5.0%</td>
                    <td className="px-4 py-2 text-center">27.0%</td>
                    <td className="px-4 py-2 text-center">68.0%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">5.5%</td>
                    <td className="px-4 py-2 text-center">28.0%</td>
                    <td className="px-4 py-2 text-center">66.5%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6.8%</td>
                    <td className="px-4 py-2 text-center font-bold">28.0%</td>
                    <td className="px-4 py-2 text-center font-bold">65.2%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition B/C roads jumped from 5.0% in 2022 to 6.8% in 2024 — a 36% increase in two years.
              Good-condition B/C roads fell from 68.0% to 65.2%. Data is collected annually in one direction only.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">Why spend is AMBER despite strong prevention</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-amber-400">£7.15m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">£7.15m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">86.7%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              North Somerset matches its DfT allocation pound for pound in 2025/26 — unlike 2024/25, when it
              spent <span className="text-emerald-400">£9.59m against £7.99m allocated</span>. The flat
              projection contributes to the AMBER spend scorecard, even as the council maintains a
              prevention-first approach that reached over 91% preventative maintenance in 2023/24.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road blind spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 650km local-road picture
          </h2>
          <p className="text-surface-600 mb-8">
            59% of the network is unclassified roads — surveyed on a two-year rolling cycle
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4.7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center">4.2%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">3.7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center">3.9%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4.1%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The two-year survey cycle</h3>
              <p className="text-surface-600 text-sm mb-3">
                North Somerset&apos;s report states U-road condition data is &ldquo;collected every two years,
                with 50% collated in year 1 and 50% collated in year 2.&rdquo; Smaller residential roads that
                standard survey vehicles cannot access receive an additional survey every four years.
              </p>
              <p className="text-surface-600 text-sm">
                The council notes U-roads in good condition rose from 74.1% to 77.6% between 2020 and 2024 on
                SCANNER data — yet it simultaneously reports more potholes and deterioration on local streets.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The lived-experience gap</h3>
              <p className="text-amber-800 text-sm mb-3">
                From 2020 to 2024, U-roads in poor (RED) condition dropped slightly on paper — from 4.7% to
                4.1%. But the council acknowledges residents, councillors and its own teams are seeing the
                opposite on the ground.
              </p>
              <p className="text-amber-800 text-sm">
                For a pothole claim on a residential street, the question is not the network average — it is
                whether the council knew about your specific defect and acted within a reasonable time.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing
              the condition of its roads. For North Somerset&apos;s unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — year one or year two of the rolling cycle?</li>
              <li>• If it is a smaller residential street, was it covered by the four-year supplementary survey?</li>
              <li>• Did the council receive prior reports of the defect via My Roads or FixMyStreet?</li>
              <li>• Can SCANNER data on a two-year cycle reasonably capture a pothole that formed between surveys?</li>
            </ul>
            <p className="text-white font-medium">
              A council that admits its survey data does not match what people see on local streets faces a
              harder argument that it could not reasonably have known about your defect.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            26,429 potholes in five years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes from North Somerset&apos;s Smart Gangs — and the 2025/26 surge
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (estimate)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">4,512</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">4,734</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">5,124</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">5,437</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">6,622</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">~8,600</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total (2020/21–2024/25)</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">26,429</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Smart Gangs and expanded crews</h3>
              <p className="text-surface-600 text-sm">
                North Somerset&apos;s Smart Gangs are multi-skilled teams fixing potholes, kerbs, signs and
                drainage. In 2024/25 they repaired over 6,600 potholes, up from 5,437 the previous year. For
                2025/26 the council is adding a dedicated reactive maintenance crew and a gully emptying team
                to respond faster to urgent defects.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">First-time permanent repairs</h3>
              <p className="text-amber-800 text-sm">
                The council states it adopts a policy of &ldquo;delivering first-time permanent repairs wherever
                possible&rdquo; rather than temporary patches requiring frequent revisits. That is positive for
                road users — but a permanent repair on a neighbouring pothole does not help your claim if the
                defect that damaged your vehicle was left untreated.
              </p>
            </div>
          </div>

          {/* Resurfacing contrast */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Reactive repairs rising, resurfacing falling</h3>
            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-rose-400">22 miles</div>
                <div className="text-brand-300 text-sm">Projected resurfacing 2025/26</div>
                <div className="text-brand-400 text-xs mt-1">Down from 28.8 miles in 2020/21</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">12.1 miles</div>
                <div className="text-brand-300 text-sm">Actually resurfaced 2023/24</div>
                <div className="text-brand-400 text-xs mt-1">The lowest figure in the five-year table</div>
              </div>
            </div>
            <p className="text-brand-200">
              More pothole filling and less resurfacing suggests the council is managing deterioration reactively
              on a network where B/C RED ratings are climbing — exactly the pattern where defects form between
              scheduled inspections.
            </p>
          </div>
        </div>
      </section>

      {/* The SCANNER disconnect admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The council&apos;s own SCANNER admission
          </h2>
          <p className="text-surface-600 mb-8">
            North Somerset publicly acknowledges its survey data does not match what road users experience
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="While SCANNER data shows that overall road condition — particularly of unclassified (U) roads — is improving, this doesn't always reflect what road users are experiencing. However, residents, councillors, and our own teams are reporting more potholes, surface wear, and deterioration — especially on local streets. This disconnect between the data and people's lived experience is a growing concern."
              source="North Somerset Council Local Highway Maintenance Transparency Report (June 2025)"
            />
            <QuoteCard
              quote="Their modern, high definition image-based approach marks a step-change from traditional SCANNER surveys, which often fail to reflect the deterioration residents and councillors report — especially on local roads."
              source="North Somerset Council — Gaist partnership case study, transparency report (June 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What this admission means</h3>
              <p className="text-surface-600 text-sm mb-3">
                North Somerset has formally documented that its official condition records understate the
                deterioration people report on local streets. It is partnering with Gaist to move beyond
                SCANNER ahead of the national PAS 2161 rollout in 2026/27.
              </p>
              <p className="text-surface-600 text-sm">
                For your claim, that is evidence the council knows its existing survey regime has blind spots
                — particularly on the U-roads where most residential driving happens.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions worth asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your road on a SCANNER survey cycle year, or in the gap?</li>
                <li>• Had councillors or residents reported deterioration on your street before your incident?</li>
                <li>• If SCANNER missed the defect, what did the council&apos;s routine safety inspections record?</li>
                <li>• Did the council act on reports via My Roads within its stated response times?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Spending history */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five years of highways spending
          </h2>
          <p className="text-surface-600 mb-8">
            Capital, revenue and the preventative-reactive split from the council&apos;s published figures
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital (£m)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend (£m)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue (£m)</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">7.18</td>
                  <td className="px-4 py-3 text-center">8.43</td>
                  <td className="px-4 py-3 text-center">2.03</td>
                  <td className="px-4 py-3 text-center">91.5%</td>
                  <td className="px-4 py-3 text-center">8.5%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">6.14</td>
                  <td className="px-4 py-3 text-center">7.39</td>
                  <td className="px-4 py-3 text-center">2.23</td>
                  <td className="px-4 py-3 text-center">89.8%</td>
                  <td className="px-4 py-3 text-center">10.2%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">4.85</td>
                  <td className="px-4 py-3 text-center">7.35</td>
                  <td className="px-4 py-3 text-center">2.33</td>
                  <td className="px-4 py-3 text-center">89.4%</td>
                  <td className="px-4 py-3 text-center">10.6%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">8.59</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">11.09</td>
                  <td className="px-4 py-3 text-center">2.50</td>
                  <td className="px-4 py-3 text-center">91.9%</td>
                  <td className="px-4 py-3 text-center">8.1%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">7.99</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">9.59</td>
                  <td className="px-4 py-3 text-center">2.70</td>
                  <td className="px-4 py-3 text-center">90.3%</td>
                  <td className="px-4 py-3 text-center">9.7%</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center font-bold">7.15</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-600">7.15</td>
                  <td className="px-4 py-3 text-center">3.10</td>
                  <td className="px-4 py-3 text-center">86.7%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">13.3%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Revenue split shifting in 2025/26</h3>
              <p className="text-surface-600 text-sm">
                Of the £3.1m revenue budget for 2025/26, 56% is planned for preventative work and 44% for
                reactive maintenance — a deliberate increase in reactive spending to fund the new maintenance
                crew and gully emptying team. Overall, 86.7% of highways maintenance investment remains
                preventative, but the reactive share is the highest in the five-year table.
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">GREEN best practice</h3>
              <p className="text-emerald-800 text-sm">
                North Somerset earns GREEN for best practice — ISO-aligned asset management, bi-annual Value
                for Money reviews through the Future Highways Research Group, and procurement delivering 10–15%
                savings. Expect a well-documented Section 58 defence; your evidence needs to be equally precise.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN condition means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against a GREEN-condition, AMBER-rated council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: North Somerset is better run than many authorities — here is how that shapes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN condition scorecard — network-wide SCANNER averages remain strong
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN best practice — documented asset management, VfM benchmarking, ISO alignment
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-roads stable at 1.4% RED with annual SCANNER surveys
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 86.7% preventative maintenance projected for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> First-time permanent repair policy — structured reactive response
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence on A-roads with recent survey data. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits SCANNER data does not match lived experience on local streets
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C RED condition up 36% since 2022 (5.0% → 6.8%)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 59% of network on two-year U-road survey cycle — plus four-year gap for smaller streets
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 26,429 potholes filled in five years — 8,600 projected for 2025/26 alone
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Resurfacing fell to 12.1 miles in 2023/24 while reactive repairs climb
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER spend — 2025/26 capital matches allocation without topping up
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN condition and best-practice scorecards, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (My Roads, FixMyStreet) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, the two-year survey gap and SCANNER disconnect are your strongest arguments</li>
              <li>• Whether the defect was on a B/C road where RED ratings are climbing fastest</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites North Somerset&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to North Somerset first
          </h2>
          <p className="text-surface-600 mb-6">
            North Somerset encourages all road users to report defects via its My Roads service. The council
            states that every report helps it prioritise work and target resources effectively. Reporting
            creates a dated record on the council&apos;s system — useful if the defect was known before your
            incident, or if the council failed to repair it within a reasonable time.
          </p>
          <a
            href="https://www.n-somerset.gov.uk/myroads"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to North Somerset Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Use the online form with location, photos and contact details. Keep your reference number and any confirmation emails.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in North Somerset?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A GREEN-rated council demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No SCANNER disconnect argument</li>
                <li>• No two-year U-road survey gap cited</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ B/C RED rise documented (5.0% → 6.8%)</li>
                <li>✅ Council&apos;s SCANNER admission cited</li>
                <li>✅ 26,429 repairs in five years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to North Somerset</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=north-somerset"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my North Somerset claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                North Somerset has a GREEN condition scorecard — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT GREEN condition rating reflects network-wide SCANNER averages, but North Somerset&apos;s
                own transparency report admits that data &ldquo;doesn&apos;t always reflect what road users are
                experiencing.&rdquo; Section 58 turns on whether the specific defect that damaged your vehicle was
                reasonably inspected and repaired — not on a headline scorecard. B and C roads in RED condition rose
                from 5.0% in 2022 to 6.8% in 2024, and the council filled 6,622 potholes in 2024/25 alone.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 650km — 59% of North Somerset&apos;s 1,111km road network — and condition data is
                collected every two years, with 50% surveyed in year one and 50% in year two. Smaller residential
                roads inaccessible to standard surveys receive an additional survey every four years. At the last five
                comparable readings, 3.7–4.7% of U-roads were in RED condition.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the council&apos;s admission about SCANNER data help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Potentially, yes. North Somerset states that residents, councillors and its own teams are
                &ldquo;reporting more potholes, surface wear, and deterioration — especially on local streets,&rdquo;
                and describes a &ldquo;disconnect between the data and people&apos;s lived experience.&rdquo; It also
                notes SCANNER surveys &ldquo;often fail to reflect the deterioration residents and councillors report
                — especially on local roads.&rdquo; That is documented acknowledgement that official condition records
                may understate defects on the road where you were damaged.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is North Somerset&apos;s spend scorecard AMBER when it invests heavily in prevention?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT Spend rating reflects how capital investment compares to the funding allocation. For 2025/26
                North Somerset projects £7.15m of capital spend against a £7.15m DfT allocation — matching it exactly,
                not exceeding it as many GREEN-rated authorities do. Previous years saw higher spend (£9.59m in 2024/25
                against £7.99m allocated), but the projected flat match for 2025/26 contributes to the AMBER spend flag.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                North Somerset plans 8,600 pothole repairs in 2025/26 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. The council projects approximately 8,600 pothole repairs in 2025/26, up from 6,622 in 2024/25 and
                well above the 4,500–6,500 range of earlier years. It attributes the rise to more reported defects and
                expanded reactive crews — not to a sudden improvement in underlying road condition. A network producing
                defects at that rate is one where potholes routinely form between inspections.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Who do I claim against for pothole damage in North Somerset?
              </h3>
              <p className="text-surface-600 text-sm">
                North Somerset Council. It is the unitary highway authority for the district&apos;s 1,111km of local
                roads, from the A370 and A38 linking Weston-super-Mare, Clevedon and Portishead down to residential
                unclassified streets. The M5 through North Somerset is managed by National Highways, not the council.
                Claims are made under Section 41 of the Highways Act 1980.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Should I report the pothole to the council before claiming?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. North Somerset encourages road users to report defects via its My Roads service at
                n-somerset.gov.uk/myroads. Reporting creates a dated record on the council&apos;s system. If the same
                defect was reported before your incident, or if the council failed to repair it within a reasonable time
                after notice, that strengthens your claim under Section 58.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://n-somerset.gov.uk/my-services/parking-travel-roads/roads-streets/road-repairs/our-highway-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              North Somerset Council Local Highway Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
